/**
 * Cadre d'une organisation : menu à gauche (comme l'espace partenaire de Booking ou la fiche
 * d'établissement de Google), barre du haut avec le changement d'organisation et le compte.
 */
import { useEffect, useState } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getManagedOrganizations } from '@web/features/auth/services/userApi'
import { writeLastManagedOrgId } from '@web/features/auth/utils/lastManagedOrgStorage'
import { useOrganization } from '@web/features/organizations/hooks/useOrganization'
import brandLogo from '@web/img/Logo-Refuge-500.png'
import { UserMenu } from './UserMenu'
import { publicLocationUrl } from '../utils/links'
import { OrgContext } from '../utils/orgContext'

const NAV = [
  { to: '', icon: 'mdi-view-dashboard-outline', key: 'nav.tableau_de_bord', end: true },
  { to: 'fiche', icon: 'mdi-card-account-details-outline', key: 'nav.fiche' },
  { to: 'lieux', icon: 'mdi-home-group', key: 'nav.lieux' },
  { to: 'equipements', icon: 'mdi-shower-head', key: 'nav.equipements' },
  { to: 'tarifs', icon: 'mdi-cash-multiple', key: 'nav.tarifs' },
  { to: 'carte', icon: 'mdi-map-outline', key: 'nav.carte' },
  { to: 'sentiers', icon: 'mdi-map-marker-path', key: 'nav.sentiers_parcours' },
  { to: 'acces', icon: 'mdi-account-key-outline', key: 'nav.acces_clients' },
  { to: 'avis', icon: 'mdi-comment-text-multiple-outline', key: 'nav.avis' },
  { to: 'photos', icon: 'mdi-image-multiple-outline', key: 'nav.photos' },
] as const

export function OrgLayout() {
  const { t } = useTranslation('gestion')
  const { orgId: raw } = useParams()
  const orgId = Number(raw)
  const location = useLocation()
  const [navOpen, setNavOpen] = useState(false)
  const { data: org, error } = useOrganization(Number.isFinite(orgId) ? orgId : null)
  const { data: managed } = useQuery({
    queryKey: ['managedOrganizations', ''],
    queryFn: ({ signal }) => getManagedOrganizations({ signal }),
  })

  useEffect(() => {
    if (Number.isFinite(orgId)) writeLastManagedOrgId(orgId)
  }, [orgId])
  useEffect(() => setNavOpen(false), [location.pathname])

  if (!Number.isFinite(orgId)) return <Navigate to="/" replace />

  const isMap = location.pathname.endsWith('/carte')
  const orgs = managed?.organizations ?? []
  const canSwitch = Boolean(managed?.is_staff) || orgs.length > 1

  return (
    <OrgContext.Provider value={{ orgId, orgName: org?.name ?? '' }}>
      <div className={`g-shell${isMap ? ' g-shell--map' : ''}${navOpen ? ' g-shell--nav-open' : ''}`}>
        <aside className="g-nav">
          <Link to="/" className="g-nav__brand">
            <img src={brandLogo} alt="" className="g-nav__logo" />
            <span>{t('app.titre_court')}</span>
          </Link>
          <div className="g-nav__org">
            <span className="g-nav__org-name" title={org?.name}>
              {org?.name ?? '…'}
            </span>
            {canSwitch && (
              <Link to="/" className="g-nav__switch">
                {t('nav.changer')}
              </Link>
            )}
          </div>
          <nav className="g-nav__links">
            {NAV.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={'end' in item ? item.end : false}
                className={({ isActive }) => `g-nav__link${isActive ? ' g-nav__link--active' : ''}`}
              >
                <span className={`mdi ${item.icon}`} aria-hidden />
                {t(item.key)}
              </NavLink>
            ))}
          </nav>
          <div className="g-nav__soon">
            <span className="g-nav__soon-title">{t('nav.bientot')}</span>
            <span className="g-nav__soon-item">
              <span className="mdi mdi-snowflake" aria-hidden /> {t('nav.etat_sentiers')}
            </span>
          </div>
          {org && (
            <a className="g-nav__public" href={publicLocationUrl({ id: org.id, type: 'organization' })} target="_blank" rel="noreferrer">
              <span className="mdi mdi-open-in-new" aria-hidden /> {t('nav.voir_fiche_publique')}
            </a>
          )}
        </aside>
        <div className="g-nav__backdrop" onClick={() => setNavOpen(false)} aria-hidden />
        <div className="g-main">
          <header className="g-top">
            <button type="button" className="g-top__menu btn btn--icon" onClick={() => setNavOpen(true)} aria-label={t('nav.menu')}>
              <span className="mdi mdi-menu" aria-hidden />
            </button>
            <span className="g-top__org">{org?.name}</span>
            <span className="g-spacer" />
            <UserMenu />
          </header>
          <div className={isMap ? 'g-content g-content--full' : 'g-content'}>
            {error ? <p className="g-alert g-alert--error">{error.message}</p> : <Outlet />}
          </div>
        </div>
      </div>
    </OrgContext.Provider>
  )
}
